/**
 * Global select-all on the React hit list.
 *
 * ItemsController is not started when NEXT_HIT_LIST is on, so the React list
 * has to drive WatchlistService itself. Global mode hides "Watchlist (N)".
 * A search, filter, or category change ends that mode and keeps the concrete
 * keys (fetch-item-controller.js#invalidateSelectAll).
 */

export interface WatchlistGlobalSelectionApi {
    setGlobalSelection?: (active: boolean) => void;
    isGlobalSelection?: () => boolean;
}

/** User restrictions. Pagination and sort do not end global mode. */
const RESTRICTION_KEYS = [
    'keyword',
    'category',
    'selectionId',
    'compliant',
    'errorKey',
    'warningKey',
    'reviewErrorKey',
    'reviewWarningKey',
    'publicationDestination',
    'publicationStates',
    'publicationTaskId',
    'recipient',
    'reviewStatus',
    'reviewer',
    'tags',
    'taskId',
    'bulkSearchId',
    'bulkSubscriptionId',
] as const;

export function hitListRestrictionSignature(
    query: Record<string, unknown> | null | undefined,
): string {
    if (!query) {
        return '';
    }
    const parts: string[] = [];
    for (const key of RESTRICTION_KEYS) {
        const value = query[key];
        if (value === undefined || value === null || value === '') {
            continue;
        }
        parts.push(`${key}=${String(value)}`);
    }
    return parts.join('&');
}

/**
 * The first observation only records the current result set. A later
 * difference ends global mode. Sort and page keys are not part of the
 * signature, so they never end it.
 */
export function shouldEndGlobalSelection(
    previousSignature: string | null,
    nextSignature: string,
    isGlobal: boolean,
): boolean {
    if (!isGlobal || previousSignature === null) {
        return false;
    }
    return previousSignature !== nextSignature;
}

/**
 * `ALL` hides the nav counter. `EXIT_GLOBAL` shows it again without
 * clearing the stored keys. `NONE` also leaves global mode when it is on
 * (WatchlistService.clearWatchlist does the same).
 */
export function applyGlobalSelectionEvent(
    items: unknown,
    service: WatchlistGlobalSelectionApi | null | undefined,
): 'enter' | 'exit' | 'unchanged' {
    if (items === 'ALL') {
        service?.setGlobalSelection?.(true);
        return 'enter';
    }
    if (items === 'EXIT_GLOBAL') {
        service?.setGlobalSelection?.(false);
        return 'exit';
    }
    if (items === 'NONE' && service?.isGlobalSelection?.()) {
        service.setGlobalSelection?.(false);
        return 'exit';
    }
    return 'unchanged';
}
